/**
 * LinkedIn OAuth Callback Handler
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle, Linkedin, Building2, User } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface LinkedInAccount {
  id: string;
  type: 'person' | 'organization';
  name: string;
  profilePicture?: string;
  vanityName?: string;
}

export default function LinkedInCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'selecting'>('loading');
  const [message, setMessage] = useState('Connecting your LinkedIn account...');
  const [accounts, setAccounts] = useState<LinkedInAccount[]>([]);
  const [tempToken, setTempToken] = useState<string>('');
  const [saving, setSaving] = useState<string | null>(null);

  // Check if this is running in a popup window
  const isPopup = window.opener && window.opener !== window;

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      setStatus('error');
      setMessage(errorDescription || 'Authorization was denied or cancelled');

      if (isPopup) {
        // Send error message to parent window
        window.opener.postMessage({
          type: 'LINKEDIN_OAUTH_ERROR',
          error: errorDescription || 'Authorization was denied or cancelled'
        }, window.location.origin);

        setTimeout(() => {
          window.close();
        }, 2000);
      }
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No authorization code received');

      if (isPopup) {
        // Send error message to parent window
        window.opener.postMessage({
          type: 'LINKEDIN_OAUTH_ERROR',
          error: 'No authorization code received'
        }, window.location.origin);

        setTimeout(() => {
          window.close();
        }, 2000);
      }
      return;
    }

    try {
      // Use the new get-accounts endpoint for account selection flow
      const response = await apiClient.post('/linkedin/oauth/get-accounts', { code });

      if (!response.success || response.error) {
        throw ApiError.fromResult(response, 'API request failed');
      }

      // The API client should now properly extract the data
      const { tempToken: token, availableAccounts } = response.data || {};

      if (!token || !availableAccounts || availableAccounts.length === 0) {
        throw new Error('No accounts available');
      }

      setTempToken(token);
      setAccounts(availableAccounts);
      setStatus('selecting');
      setMessage(`Found ${availableAccounts.length} LinkedIn account(s). Please select one...`);

      // If NOT in popup, redirect to separate account selection page
      if (!isPopup) {
        setTimeout(() => {
          navigate(`/home/social-media/linkedin/select-account?token=${token}`, {
            state: { accounts: availableAccounts, tempToken: token },
          });
        }, 1500);
      }
    } catch (error: any) {
      setStatus('error');
      setMessage(
        error.message || error.response?.data?.error || 'Failed to connect LinkedIn account'
      );

      if (isPopup) {
        // Send error message to parent window
        window.opener.postMessage({
          type: 'LINKEDIN_OAUTH_ERROR',
          error: error.message || error.response?.data?.error || 'Failed to connect LinkedIn account'
        }, window.location.origin);

        setTimeout(() => {
          window.close();
        }, 2000);
      }
    }
  };

  const handleSelectAccount = async (account: LinkedInAccount) => {
    setSaving(account.id);
    try {
      // Scope the new account to the business account selected when connecting.
      const businessAccountId = localStorage.getItem('selectedBusinessAccountId') || null;
      const response = await apiClient.post('/linkedin/oauth/connect-selected', {
        tempToken,
        selectedAccount: {
          type: account.type,
          id: account.id
        },
        business_account_id: businessAccountId
      });

      if (response.error) {
        throw ApiError.fromResult(response);
      }

      setStatus('success');
      setMessage(`Successfully connected ${account.name}!`);

      if (isPopup) {
        // Send success message to parent window and close popup
        window.opener.postMessage({
          type: 'LINKEDIN_OAUTH_SUCCESS',
          data: response.data
        }, window.location.origin);

        setTimeout(() => {
          window.close();
        }, 1000);
      } else {
        // Regular redirect for non-popup flow
        setTimeout(() => {
          navigate('/home/social-media/linkedin');
        }, 1500);
      }
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message || error.response?.data?.error || 'Failed to save LinkedIn account');
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="bg-blue-700 p-4 rounded-full text-white inline-block">
              <Linkedin className="h-8 w-8" />
            </div>
          </div>
          <CardTitle>LinkedIn Connection</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-700" />
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'selecting' && (
            <div className="space-y-4">
              <p className="text-lg font-semibold text-blue-700">{message}</p>
              <div className="grid gap-4 md:grid-cols-2">
                {accounts.map((account) => (
                  <Card
                    key={account.id}
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      saving === account.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => handleSelectAccount(account)}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col items-center gap-3">
                        {account.profilePicture ? (
                          <img
                            src={account.profilePicture}
                            alt={account.name}
                            className="h-16 w-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-xl">
                            {account.name?.[0]?.toUpperCase() || 'LI'}
                          </div>
                        )}
                        <div className="text-center">
                          <p className="font-semibold">{account.name}</p>
                          <Badge variant={account.type === 'organization' ? 'default' : 'secondary'} className="mt-1">
                            {account.type === 'organization' ? (
                              <><Building2 className="h-3 w-3 mr-1" /> Company</>
                            ) : (
                              <><User className="h-3 w-3 mr-1" /> Personal</>
                            )}
                          </Badge>
                          {account.vanityName && (
                            <p className="text-xs text-muted-foreground mt-1">
                              /{account.vanityName}
                            </p>
                          )}
                        </div>
                        <Button
                          className="w-full mt-2"
                          disabled={saving !== null}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAccount(account);
                          }}
                        >
                          {saving === account.id ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            'Select This Account'
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
              <p className="text-lg font-semibold text-green-700">{message}</p>
              <p className="text-sm text-muted-foreground">
                {isPopup ? 'Closing popup...' : 'Redirecting...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-red-500" />
              <p className="text-lg font-semibold text-red-700">Connection Failed</p>
              <p className="text-sm text-muted-foreground">{message}</p>
              {!isPopup && (
                <div className="flex gap-2 justify-center mt-4">
                  <Button onClick={() => navigate('/home/social-media/linkedin')} variant="outline">
                    Go Back
                  </Button>
                  <Button onClick={handleCallback}>Try Again</Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
